import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { publicAPI } from '../lib/api';
import { useT } from '../lib/i18n';
import { BACKEND_URL as API } from '../lib/config';
import { PageHero } from './ModernPage';
import { ModernAlbumCards } from './layouts/ModernBlocks';
import { Lightbox } from '../pages/GalleryPage';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» versions of /gallery and /album/:id (s140) — same data as the
// default pages (Gallery manager photos; Gallery Albums + album photos), with
// the internal-page standard: PageHero, 1440 shell, serif titles, 3px corners,
// the /gallery lightbox. Rendered only when settings.active_theme === 'modern'.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

function Tile({ src, onClick, label, children }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="group flex flex-col gap-3 text-left">
      <span className="block aspect-[4/3] overflow-hidden rounded-[3px]" style={{ background: GRAPHITE }}>
        {src && <img src={img(src)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
      </span>
      {children}
    </button>
  );
}

/* ── /gallery ─────────────────────────────────────────────────────────── */
export function ModernGalleryPage() {
  const tt = useT();
  const [items, setItems] = React.useState([]);
  const [cfg, setCfg] = React.useState({});
  const [cat, setCat] = React.useState('');
  const [lb, setLb] = React.useState(-1);
  React.useEffect(() => {
    publicAPI.getGallery().then(r => setItems((r.data || []).filter(g => g.image))).catch(() => {});
    fetch(`${API}/api/public/aurex/aurex_gallery_cfg`).then(r => r.json()).then(d => setCfg((d && d.config) || {})).catch(() => {});
  }, []);
  const cats = [...new Set(items.map(g => tt(g.category)).filter(Boolean))];
  const shown = items.filter(g => !cat || tt(g.category) === cat);
  const lbItems = shown.map(g => ({ ...g, title: tt(g.title), summary: tt(g.summary) }));
  const pill = (active) => ({ borderColor: active ? 'var(--color-heading, #1a2332)' : 'rgba(20,26,58,.15)', background: active ? 'var(--color-heading, #1a2332)' : 'transparent', color: active ? '#fff' : 'var(--color-heading, #1a2332)' });
  return (
    <div data-testid="gallery-page" className="bg-white">
      <PageHero title={tt(cfg.title) || 'Gallery'} summary={tt(cfg.subtitle)} banner={items[0] && items[0].image} />
      <div className={`${shell} py-14 lg:py-20 flex flex-col gap-10`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          {cats.length > 1 ? (
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter photos">
              {['', ...cats].map(c => (
                <button key={c || 'all'} type="button" role="tab" aria-selected={cat === c} onClick={() => setCat(c)}
                  className="px-4 py-1.5 rounded-full border text-[13px] font-semibold transition-colors duration-150 active:scale-[0.97]" style={pill(cat === c)} data-testid={`gallery-page-tab-${c || 'all'}`}>{c || 'All'}</button>
              ))}
            </div>
          ) : <span />}
          <Link to="/gallery_albums" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start" style={{ color: 'var(--accent-strong, #b86500)' }}>Browse albums <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {shown.length ? (
          <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((g, i) => (
              <Tile key={g.id} src={g.image} onClick={() => setLb(i)} label={`Enlarge ${tt(g.title)}`}>
                {tt(g.category) && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{tt(g.category)}</span>}
                <span className="font-semibold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.25rem', color: 'var(--color-heading, #1a2332)' }}>{tt(g.title)}</span>
                {tt(g.summary) && <span className="text-[15px] leading-relaxed line-clamp-1" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(g.summary)}</span>}
              </Tile>
            ))}
          </div>
        ) : <p className="text-center py-16" style={{ color: 'var(--color-body-text, #475569)' }}>No photos yet.</p>}
      </div>
      {lb >= 0 && <Lightbox items={lbItems} currentIndex={lb} onClose={() => setLb(-1)} onNext={() => setLb(i => (i + 1) % lbItems.length)} onPrev={() => setLb(i => (i - 1 + lbItems.length) % lbItems.length)} />}
    </div>
  );
}

/* ── /album/:albumId ──────────────────────────────────────────────────── */
export function ModernAlbumPage() {
  const tt = useT();
  const { albumId } = useParams();
  const [album, setAlbum] = React.useState(null);
  const [photos, setPhotos] = React.useState([]);
  const [lb, setLb] = React.useState(-1);
  React.useEffect(() => {
    setAlbum(null); setPhotos([]); setLb(-1);
    if (albumId) publicAPI.getAlbumPhotos(albumId).then(r => { setAlbum(r.data.album); setPhotos(r.data.photos || []); }).catch(() => setAlbum(false));
    window.scrollTo(0, 0);
  }, [albumId]);
  if (album === null) return <div className="pt-28 min-h-[60vh]" />;
  if (album === false) return <div className="pt-28 text-center" style={{ color: 'var(--color-body-text, #475569)' }}>Album not found.</div>;
  const title = tt(album.title);
  const lbItems = photos.map(p => ({ ...p, image: p.image || p.url, title, summary: plain(tt(p.caption)) }));
  return (
    <div data-testid="layout-sub-gallery" className="bg-white">
      <PageHero title={title} summary={plain(tt(album.description))} banner={album.cover_image}
        trail={[{ label: 'Home', to: '/' }, { label: 'Gallery Albums', to: '/gallery_albums' }, { label: title }]} />
      <div className={`${shell} py-14 lg:py-20 flex flex-col gap-8`}>
        <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.16em', color: 'var(--accent-strong, #b86500)' }}>{photos.length} photo{photos.length === 1 ? '' : 's'}</span>
        {photos.length ? (
          <div className="grid gap-4 lg:gap-6 grid-cols-2 lg:grid-cols-3">
            {photos.map((p, i) => (
              <button key={p.id} type="button" onClick={() => setLb(i)} aria-label={`Open photo ${i + 1}`} className="group block aspect-[4/3] overflow-hidden rounded-[3px]" style={{ background: GRAPHITE }} data-testid={`gallery-thumb-${i}`}>
                <img src={img(p.image || p.url)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
              </button>
            ))}
          </div>
        ) : <p className="py-10" style={{ color: 'var(--color-body-text, #475569)' }}>No photos in this album yet.</p>}
      </div>
      <section className="border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
        <div className={`${shell} py-16 lg:py-24 flex flex-col gap-10`}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', color: 'var(--color-heading, #1a2332)' }}>More <em style={{ fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>albums</em></h2>
            <Link to="/gallery_albums" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }}>All albums <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <ModernAlbumCards exclude={album.id} limit={3} />
        </div>
      </section>
      {lb >= 0 && <Lightbox items={lbItems} currentIndex={lb} onClose={() => setLb(-1)} onNext={() => setLb(i => (i + 1) % lbItems.length)} onPrev={() => setLb(i => (i - 1 + lbItems.length) % lbItems.length)} />}
    </div>
  );
}
